/**
 * @file FullscreenConsent.ts
 * @role Owns the one-time "Play fullscreen?" consent modal (#fullscreen-consent in index.html).
 *       Asks touch-device players once, remembers the answer, and turns an acceptance into a
 *       fullscreen request on the same gesture — the only moment a browser will grant it.
 * @owns Showing/hiding the modal, its two button handlers, and persisting the answer.
 * @reads document, an injected FullscreenService, and an injected ConsentStore.
 * @neverDoes Render game content. Unlock audio (Bootstrapper's global pointerdown listener
 *            already does that on the same gesture). Block the game when fullscreen is refused.
 * @invariants 8 (no game logic), 13 (constructed by Bootstrapper, never a module singleton).
 * @see index.html (#fullscreen-consent), src/core/viewport/FullscreenService.ts
 */
import type { FullscreenService } from '@core/viewport/FullscreenService';

export const FULLSCREEN_CONSENT_KEY = 'slot-game.fullscreen-consent';

const ANSWERS = new Set(['accepted', 'declined']);

/** The slice of Storage we use; lets tests inject a fake and survives no-storage envs. */
export interface ConsentStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface FullscreenConsentOptions {
  fullscreen: FullscreenService;
  /** True only on touch devices. Desktop players are never asked. */
  coarsePointer: boolean;
  /**
   * Ignore any stored answer for this load, so the modal can be retested. Set only by the
   * dev/mock-preview `?fsConsent=reset` flag; always false in production.
   */
  ignoreStoredAnswer?: boolean;
  store?: ConsentStore;
}

function defaultStore(): ConsentStore | undefined {
  try {
    // localStorage can throw in sandboxed/private contexts, not just be absent.
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export class FullscreenConsent {
  private readonly fullscreen: FullscreenService;
  private readonly coarsePointer: boolean;
  private readonly store: ConsentStore | undefined;
  private readonly ignoreStoredAnswer: boolean;

  constructor(options: FullscreenConsentOptions) {
    this.fullscreen = options.fullscreen;
    this.coarsePointer = options.coarsePointer;
    this.ignoreStoredAnswer = options.ignoreStoredAnswer ?? false;
    this.store = options.store ?? defaultStore();
  }

  /**
   * Resolves once the player has answered — or immediately when we should not ask.
   * Resolves on every path, so boot can never hang here.
   */
  ask(): Promise<void> {
    // Never ask a desktop player, a browser that cannot honour it (iPhone Safari), or a player
    // who is already fullscreen. Checked before touching the DOM so the desktop path costs
    // nothing on the boot critical path.
    if (!this.coarsePointer || !this.fullscreen.supported || this.fullscreen.active) {
      return Promise.resolve();
    }

    // Already answered: honour it rather than asking again.
    const answer = this.storedAnswer();
    if (answer !== undefined) {
      if (answer === 'accepted') this.requestOnNextGesture();
      return Promise.resolve();
    }

    if (typeof document === 'undefined') return Promise.resolve();
    const modal = document.getElementById('fullscreen-consent');
    const accept = document.getElementById('fullscreen-consent-accept');
    const decline = document.getElementById('fullscreen-consent-decline');
    if (!modal || !accept || !decline) return Promise.resolve();

    modal.hidden = false;
    try {
      accept.focus();
    } catch {
      // Focusing is a nicety; never let it stop the modal resolving.
    }

    return new Promise<void>((resolve) => {
      // Deliberately local, not instance state: a second ask() gets its own flag, so it can
      // never inherit a settled one and leave the modal open with no way to resolve.
      let settled = false;
      const answer = (value: 'accepted' | 'declined'): void => {
        if (settled) return;
        settled = true;
        accept.removeEventListener('click', onAccept);
        decline.removeEventListener('click', onDecline);
        if (value === 'accepted') {
          // Called synchronously and NOT awaited: awaiting would consume the user activation
          // the browser requires. A refusal is fine — the game plays windowed.
          try {
            void this.fullscreen.request();
          } catch {
            // Never block the game on a fullscreen failure.
          }
        }
        this.remember(value);
        modal.hidden = true;
        resolve();
      };
      const onAccept = (): void => answer('accepted');
      const onDecline = (): void => answer('declined');
      accept.addEventListener('click', onAccept);
      decline.addEventListener('click', onDecline);
    });
  }

  /** Persisted answers are untrusted input; only the two known values count as answered. */
  private storedAnswer(): 'accepted' | 'declined' | undefined {
    if (this.ignoreStoredAnswer) return undefined;
    try {
      const stored = this.store?.getItem(FULLSCREEN_CONSENT_KEY) ?? '';
      return ANSWERS.has(stored) ? (stored as 'accepted' | 'declined') : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * A player who already accepted still wants fullscreen on later visits, but a browser only
   * grants it from a gesture — so we cannot restore it during boot. Re-request on their next
   * tap instead of asking again. Without this, "accepted" would be a one-shot: fullscreen on
   * the load they answered, then windowed forever with no way back.
   */
  private requestOnNextGesture(): void {
    if (typeof document === 'undefined') return;
    const onGesture = (): void => {
      document.removeEventListener('pointerdown', onGesture);
      document.removeEventListener('keydown', onGesture);
      try {
        void this.fullscreen.request();
      } catch {
        // Refused or unsupported: the game simply stays windowed.
      }
    };
    document.addEventListener('pointerdown', onGesture);
    document.addEventListener('keydown', onGesture);
  }

  private remember(value: 'accepted' | 'declined'): void {
    try {
      this.store?.setItem(FULLSCREEN_CONSENT_KEY, value);
    } catch {
      // A full or blocked storage must not break the game; we simply ask again next session.
    }
  }
}
